import { Injectable, inject, signal } from '@angular/core';
import { WaypointService } from '../core/waypoint.service';
import { NavigationService } from '../core/navigation.service';
import { RealtimeVehicleToolsService } from './realtime-vehicle-tools.service';
import { RealtimeSettingsToolsService } from './realtime-settings-tools.service';
import { MapService } from '../map/map.service';
import { LocationService } from '../platform/location.service';
import { UtilsService } from '../utils/utils.service';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import { StackedModalsService } from '../modal/stacked-modals.service';
import { firstValueFrom, merge, timer } from 'rxjs';
import { filter, map, skip } from 'rxjs/operators';
import { Route, RoutePlanElement, RoutePlanType } from '../../models/route';
import { ChargeState } from '../../models/charge-state';
import { WindElement } from '../../models/wind';
import { SettingsService, Unit } from '../account/settings.service';
import { EvCalculationService } from '../core/ev-calculation.service';
import { EvCalculationFlowHandler } from '../handlers/ev-calculation-flow-handler.service';
import { Waypoint } from '../../models/range-params';
import { RouteListElement } from '../../models/route-list';
import { DisplayMode } from '../../models/display-mode';
import { SearchedRouteElement } from '../../models/searched-route-element';
import { VoiceCommandErrorDialogComponent } from '../../components/modals/voice-command-error-dialog/voice-command-error-dialog.component';
import { environment } from 'src/environments/environment';
import { PlaceSearchService } from '../map/place-search.service';
import { AudioService } from './audio.service';

/** Outcome of one tool call: what is reported back to the model, and whether the instructions need refreshing. */
interface RealtimeToolResult {
  output: Record<string, unknown>;
  stateChanged: boolean;
}

/** What the conversation is doing right now; drives the overlay status and wave animation. */
export type RealtimeVoicePhase = 'idle' | 'connecting' | 'listening' | 'user-speaking' | 'thinking' | 'speaking';

/** One line of the live transcript. `final` is false while the text is still streaming in. */
export interface RealtimeVoiceTurn {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  final: boolean;
}

/**
 * OpenAI Realtime voice copilot.
 *
 * Opens a WebRTC session to the OpenAI Realtime API (mic in, assistant audio out, events over the
 * "oai-events" data channel). The assistant drives the app through function tools (plan route,
 * waypoints, vehicle settings, zoom, start/stop navigation); after each state change the session
 * instructions are refreshed so the model sees the current waypoints, vehicle settings and route.
 *
 * Turn handling: server VAD commits user turns but does not auto-respond; a response is created
 * only once the assistant has finished speaking, and speech that starts while the assistant is
 * talking (its own echo, usually) is ignored. The session closes itself after
 * REALTIME_CONVERSATION_IDLE_TIMEOUT_MS of silence.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeVoiceService {
  private static readonly REALTIME_MODEL: string = 'gpt-realtime-mini';
  private static readonly REALTIME_CONVERSATION_IDLE_TIMEOUT_MS: number = 15000;
  private static readonly REALTIME_DEBUG_LOGGING_ENABLED: boolean = false;
  private static readonly REALTIME_SERVER_VAD_THRESHOLD: number = 0.65;
  private static readonly REALTIME_SERVER_VAD_PREFIX_PADDING_MS: number = 300;
  private static readonly REALTIME_SERVER_VAD_SILENCE_DURATION_MS: number = 700;
  private static readonly REALTIME_TRANSCRIPTION_MODEL: string = 'gpt-4o-mini-transcribe';
  private static readonly REALTIME_MAX_TURNS: number = 20;
  /** Same limit as the map POI card's GO button: start + 4 more points. */
  private static readonly MAX_ROUTE_POINTS: number = 5;
  private static readonly ROUTE_RESULT_TIMEOUT_MS: number = 20000;
  private static readonly REALTIME_MIC_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1
  };

  public readonly active = signal<boolean>(false);
  public readonly phase = signal<RealtimeVoicePhase>('idle');
  public readonly turns = signal<RealtimeVoiceTurn[]>([]);
  /** Microphone and assistant audio, exposed so the overlay can visualise their loudness. */
  public readonly inputStream = signal<MediaStream | null>(null);
  public readonly outputStream = signal<MediaStream | null>(null);

  private realtimePc: RTCPeerConnection | null = null;
  private realtimeDc: RTCDataChannel | null = null;
  private realtimeStream: MediaStream | null = null;
  private realtimeAudio: HTMLAudioElement | null = null;
  private realtimeConversationIdleTimeoutRef: ReturnType<typeof setTimeout> | null = null;
  private realtimeAssistantOutputActive: boolean = false;
  private realtimeAssistantAudioPlaying: boolean = false;
  private realtimeQueuedResponseItemId: string | null = null;
  private realtimeIgnoreCurrentInputTurn: boolean = false;
  private realtimeIgnoredInputItemIds: Set<string> = new Set<string>();

  private readonly waypointService = inject(WaypointService);
  private readonly navigationService = inject(NavigationService);
  private readonly vehicleTools = inject(RealtimeVehicleToolsService);
  private readonly settingsTools = inject(RealtimeSettingsToolsService);
  private readonly mapService = inject(MapService);
  private readonly locationService = inject(LocationService);
  private readonly utilsService = inject(UtilsService);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);
  private readonly stackedModalsService = inject(StackedModalsService);
  private readonly placeSearchService = inject(PlaceSearchService);
  private readonly settingsService = inject(SettingsService);
  private readonly evCalculationService = inject(EvCalculationService);
  private readonly evCalculationFlowHandler = inject(EvCalculationFlowHandler);
  private readonly audioService = inject(AudioService);

  public async toggle(): Promise<void> {
    if (this.active() || this.realtimePc) {
      this.stop();
      return;
    }
    await this.start();
  }

  public async start(): Promise<void> {
    this.turns.set([]);
    this.phase.set('connecting');
    this.audioService.setRealtimeVoiceActive(true);

    const systemPrompt = await this.buildSessionPrompt();
    this.debugLog("PROMPT:", systemPrompt);

    let pc: RTCPeerConnection | null = null;
    let dc: RTCDataChannel | null = null;

    const logSent = (msg: unknown) => {
      this.handleRealtimeConversationActivityMessage(msg);
      this.debugLog("SENT:", msg);
    };
    const logReceived = (msg: unknown) => {
      this.handleRealtimeConversationActivityMessage(msg);
      this.debugLog("RECEIVED:", msg);
    };

    const send = (msg: unknown) => {
      if (dc && dc.readyState === 'open') {
        dc.send(JSON.stringify(msg));
        logSent(msg);
      }
    };

    const flushQueuedRealtimeResponse = () => {
      if (!dc || !this.realtimeQueuedResponseItemId || this.realtimeAssistantOutputActive) {
        return;
      }

      send({
        type: "response.create",
        response: {
          output_modalities: ["audio"]
        }
      });
      this.realtimeQueuedResponseItemId = null;
      this.phase.set('thinking');
    };

    const queueRealtimeResponseForUserTurn = (itemId?: string) => {
      if (!itemId) {
        return;
      }

      this.realtimeQueuedResponseItemId = itemId;
      flushQueuedRealtimeResponse();
    };

    const updateSession = async () => {
      send(this.createSessionUpdateMessage(await this.buildSessionPrompt()));
    };

    /**
     * Executes the calls in order (some wait for a background place search), answers each one,
     * refreshes the instructions if app state changed, then asks for a single spoken reply.
     */
    const runToolCalls = async (calls: any[]) => {
      this.phase.set('thinking');
      // Place searches and route calculations can outlast the silence timeout; the response.create below restarts it.
      this.clearRealtimeConversationIdleTimeout();
      let stateChanged = false;

      for (const call of calls) {
        let args: any = {};
        try {
          args = JSON.parse(call.arguments || "{}");
        } catch { }

        const result = await this.executeToolCall(call.name, args);
        // The session may have been closed while a place search was in flight.
        if (this.realtimeDc !== dc) {
          return;
        }
        stateChanged ||= result.stateChanged;

        send({
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: call.call_id,
            output: JSON.stringify(result.output)
          }
        });
      }

      if (stateChanged) {
        await updateSession();
      }

      send({
        type: "response.create",
        response: {
          output_modalities: ["audio"]
        }
      });
    };

    try {
      pc = new RTCPeerConnection();
      this.realtimePc = pc;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: RealtimeVoiceService.REALTIME_MIC_AUDIO_CONSTRAINTS
      });
      this.realtimeStream = stream;
      this.inputStream.set(stream);
      stream.getTracks().forEach((t) => pc!.addTrack(t, stream));

      const audio = document.createElement("audio");
      audio.autoplay = true;
      this.realtimeAudio = audio;

      pc.ontrack = (e) => {
        audio.srcObject = e.streams[0];
        this.outputStream.set(e.streams[0]);
      };

      dc = pc.createDataChannel("oai-events");
      this.realtimeDc = dc;
      dc.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        logReceived(msg);

        if (msg.type === "input_audio_buffer.speech_started") {
          if (this.realtimeAssistantOutputActive) {
            this.realtimeIgnoreCurrentInputTurn = true;
          } else {
            // Placeholder so the user's line sits above the reply even if its transcript arrives later.
            this.upsertTurn(msg.item_id, 'user', '', false);
            this.phase.set('user-speaking');
          }
        }

        if (msg.type === "input_audio_buffer.committed") {
          if (this.realtimeIgnoreCurrentInputTurn && msg.item_id) {
            this.realtimeIgnoredInputItemIds.add(msg.item_id);
            this.realtimeIgnoreCurrentInputTurn = false;
            return;
          }

          this.realtimeIgnoreCurrentInputTurn = false;
          this.phase.set('thinking');
          queueRealtimeResponseForUserTurn(msg.item_id);
        }

        if (msg.type === "output_audio_buffer.started" || msg.type === "response.output_audio_transcript.delta") {
          this.phase.set('speaking');
        }

        if (msg.type === "output_audio_buffer.stopped" || msg.type === "response.output_audio.done") {
          if (msg.type === "output_audio_buffer.stopped") {
            this.phase.set('listening');
          }
          flushQueuedRealtimeResponse();
        }

        if (msg.type === "conversation.item.input_audio_transcription.delta") {
          this.appendToTurn(msg.item_id, msg.delta);
        }

        if (msg.type === "conversation.item.input_audio_transcription.failed") {
          this.removeTurn(msg.item_id);
        }

        if (msg.type === "conversation.item.input_audio_transcription.completed") {
          if (msg.item_id && this.realtimeIgnoredInputItemIds.has(msg.item_id)) {
            this.realtimeIgnoredInputItemIds.delete(msg.item_id);
            return;
          }
          this.debugLog("You:", msg.transcript);
          if (msg.transcript?.trim()) {
            this.updateTurn(msg.item_id, msg.transcript.trim(), true);
          } else {
            this.removeTurn(msg.item_id);
          }
        }

        if (msg.type === "response.output_audio_transcript.delta") {
          if (!this.appendToTurn(msg.item_id, msg.delta)) {
            this.upsertTurn(msg.item_id, 'assistant', msg.delta ?? '', false);
          }
        }

        if (msg.type === "response.output_audio_transcript.done") {
          this.debugLog("AI:", msg.transcript);
          this.upsertTurn(msg.item_id, 'assistant', msg.transcript ?? '', true);
        }

        if (msg.type === "response.done") {
          flushQueuedRealtimeResponse();
          const functionCalls = (msg.response?.output || []).filter((item: any) => item.type === "function_call");
          if (functionCalls.length > 0) {
            runToolCalls(functionCalls);
          }
        }
      };

      dc.onclose = () => {
        this.stop();
      };

      dc.onopen = () => {
        this.active.set(true);
        // The greeting below is requested right away.
        this.phase.set('thinking');

        send({
          type: "session.update",
          session: {
            type: "realtime",
            instructions: systemPrompt,
            output_modalities: ["audio"],
            audio: {
              input: {
                noise_reduction: {
                  type: "far_field"
                },
                // Without this the server sends no transcript of the user's speech (the overlay shows it).
                transcription: {
                  model: RealtimeVoiceService.REALTIME_TRANSCRIPTION_MODEL
                },
                turn_detection: {
                  type: "server_vad",
                  threshold: RealtimeVoiceService.REALTIME_SERVER_VAD_THRESHOLD,
                  prefix_padding_ms: RealtimeVoiceService.REALTIME_SERVER_VAD_PREFIX_PADDING_MS,
                  silence_duration_ms: RealtimeVoiceService.REALTIME_SERVER_VAD_SILENCE_DURATION_MS,
                  create_response: false,
                  interrupt_response: false
                }
              }
            },
            tools: this.getRealtimeTools(),
            tool_choice: "auto"
          }
        });

        // Prompt the assistant to speak a greeting once the session is ready
        send({
          type: "response.create",
          response: {
            output_modalities: ["audio"],
            instructions: "please say: Hi, welcome to EVNavigation! How can I help you today?"
          }
        });
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const answer = await this.requestSdpAnswer(offer.sdp!);
      // The session may have been stopped while the SDP exchange was in flight.
      if (this.realtimePc !== pc) {
        return;
      }
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
    } catch (err: any) {
      console.error(err);
      this.stop();
      this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent, {
        data: { errorMsg: err?.message || 'Failed to connect to voice service. Please try again.' },
        autoFocus: false
      });
    }
  }

  public stop(): void {
    this.clearRealtimeConversationIdleTimeout();
    this.realtimeAssistantOutputActive = false;
    this.realtimeAssistantAudioPlaying = false;
    this.realtimeQueuedResponseItemId = null;
    this.realtimeIgnoreCurrentInputTurn = false;
    this.realtimeIgnoredInputItemIds.clear();

    if (this.realtimeDc) {
      const dc = this.realtimeDc;
      this.realtimeDc = null;
      dc.onclose = null;
      try { dc.close(); } catch { }
    }
    if (this.realtimePc) {
      try { this.realtimePc.close(); } catch { }
      this.realtimePc = null;
    }
    if (this.realtimeStream) {
      this.realtimeStream.getTracks().forEach((t) => t.stop());
      this.realtimeStream = null;
    }
    if (this.realtimeAudio) {
      this.realtimeAudio.pause();
      this.realtimeAudio.srcObject = null;
      this.realtimeAudio = null;
    }

    this.inputStream.set(null);
    this.outputStream.set(null);
    this.phase.set('idle');
    this.active.set(false);
    this.audioService.setRealtimeVoiceActive(false);
  }

  private upsertTurn(id: string | undefined, role: RealtimeVoiceTurn['role'], text: string, final: boolean): void {
    if (!id) {
      return;
    }
    this.turns.update(turns => {
      const index = turns.findIndex(turn => turn.id === id);
      if (index >= 0) {
        return turns.map((turn, i) => i === index ? { ...turn, text, final } : turn);
      }
      return [...turns, { id, role, text, final }].slice(-RealtimeVoiceService.REALTIME_MAX_TURNS);
    });
  }

  /** Only touches an existing turn, so transcripts of ignored (echo) input never show up. */
  private updateTurn(id: string | undefined, text: string, final: boolean): void {
    this.turns.update(turns => turns.map(turn => turn.id === id ? { ...turn, text, final } : turn));
  }

  /** Appends a streamed delta to an existing turn; returns false when there is no such turn. */
  private appendToTurn(id: string | undefined, delta: string | undefined): boolean {
    const turn = this.turns().find(t => t.id === id);
    if (!turn) {
      return false;
    }
    this.updateTurn(id, turn.text + (delta ?? ''), false);
    return true;
  }

  private removeTurn(id: string | undefined): void {
    this.turns.update(turns => turns.filter(turn => turn.id !== id));
  }

  private async requestSdpAnswer(sdp: string): Promise<string> {
    let res: Response | null = null;
    let retries = 3;
    while (retries > 0) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      // GA interface: the offer and the session (which carries the model) go together as multipart form data.
      const body = new FormData();
      body.set("sdp", sdp);
      body.set("session", JSON.stringify({ type: "realtime", model: RealtimeVoiceService.REALTIME_MODEL }));
      try {
        res = await fetch(
          "https://api.openai.com/v1/realtime/calls",
          {
            method: "POST",
            headers: {
              // TODO: must not ship in the client bundle — replace with an ephemeral key minted by the backend.
              Authorization: `Bearer ${environment.OPENAI_API_KEY}`
            },
            body,
            signal: controller.signal
          }
        );
        clearTimeout(timeoutId);
        if (res.ok) break;
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        console.warn(`Fetch failed: ${fetchError?.message}`);
      }
      retries--;
      if (retries > 0) {
        console.warn(`OpenAI API failed, retrying... (${retries} attempts left)`);
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    if (!res || !res.ok) {
      let errorData: any;
      try {
        errorData = await res!.json();
      } catch {
        errorData = { message: res ? `HTTP ${res.status}: ${res.statusText || 'Unknown error'}` : 'Network error' };
      }
      console.error('OpenAI API error:', errorData);
      throw new Error(`OpenAI API error: ${errorData?.error?.message ?? errorData?.message}`);
    }

    return res.text();
  }

  private async executeToolCall(name: string, args: any): Promise<RealtimeToolResult> {
    this.consoleLoggerService.log("Realtime tool call", name, args);

    if (this.vehicleTools.handles(name)) {
      return this.vehicleTools.execute(name, args);
    }
    if (this.settingsTools.handles(name)) {
      return this.settingsTools.execute(name, args);
    }

    switch (name) {
      case "plan_route":
      case "add_waypoint":
        return this.addDestination(args);

      case "plan_tour":
        return this.planTour(args);

      case "skip_next_stop":
        return this.skipNextStop(args);

      case "get_route_info":
        if (this.evCalculationFlowHandler.isRouteCalculating()) {
          await this.nextRouteResult();
        }
        return { output: this.buildRouteInfo(), stateChanged: false };

      case "remove_waypoint": {
        const count = this.waypointService.getWaypointsParams().length;
        if (args.index === 0) {
          return { output: { success: false, error: "cannot_remove_start", message: "Index 0 is the start point, not a waypoint. The first waypoint is index 1." }, stateChanged: false };
        }
        if (!(args.index > 0 && args.index < count)) {
          return { output: { success: false, error: "invalid_index", message: `Waypoint indices are 1 to ${count - 1}.` }, stateChanged: false };
        }
        break;
      }
    }

    return { output: { success: true }, stateChanged: this.handleToolCall(name, args) };
  }

  /**
   * Replaces everything after the start with the stops the assistant picked (a sightseeing or
   * round tour), resolving all of them first so the route is calculated once, not per stop.
   */
  private async planTour(args: any): Promise<RealtimeToolResult> {
    const fail = (error: string, message: string): RealtimeToolResult => ({ output: { success: false, error, message }, stateChanged: false });

    const stops: string[] = (Array.isArray(args.stops) ? args.stops : []).filter((s: unknown) => typeof s === 'string' && s.trim());
    const roundTrip = args.round_trip === true;
    const maxStops = RealtimeVoiceService.MAX_ROUTE_POINTS - 1 - (roundTrip ? 1 : 0);
    if (stops.length === 0) {
      return fail("missing_stops", "Provide at least one stop.");
    }
    // Keep the first stops that fit instead of failing: a rejected call makes the model retry,
    // and every retry is one more spoken reply.
    const skipped = stops.splice(maxStops);

    const start = this.getStartPoint();
    if (!start) {
      return fail("no_start", "The start position is unknown (no GPS position and no start point).");
    }

    const places = await Promise.all(stops.map(stop => this.placeSearchService.findPlace(stop)));
    const found = places.filter(place => place != null);
    const notFound = stops.filter((_, i) => places[i] == null);
    if (found.length === 0) {
      return fail("place_not_found", `None of the stops were found: ${notFound.join(', ')}.`);
    }

    const points: RouteListElement[] = [
      start,
      ...found.map(place => ({ display_name: place.name, lat: place.lat, lng: place.lon })),
      ...(roundTrip ? [start] : [])
    ];
    const route = await this.applyAndAwaitRoute(() => this.waypointService.replaceRoute(points));

    return {
      output: {
        success: true,
        stops: found.map(place => ({ name: place.name, address: place.address })),
        not_found: notFound.length > 0 ? notFound : undefined,
        skipped_over_limit: skipped.length > 0 ? skipped : undefined,
        route
      },
      stateChanged: true
    };
  }

  /** The current start entry, or the GPS position when the route has no start yet. */
  private getStartPoint(): RouteListElement | null {
    const start = this.waypointService.getRouteList().find(el => el.wp === 0);
    if (start) {
      return { display_name: start.display_name, lat: start.lat, lng: start.lng };
    }
    const position = this.locationService.getLastPosition();
    return position ? { display_name: '', lat: position.Latitude, lng: position.Longitude } : null;
  }

  /** Applies a waypoint change and waits for the recalculated route, so the tool can report it. */
  /**
   * Skips the next waypoint or charging station during navigation, like the skip button of the
   * navigation card (NavigationPlanComponent.skipWaypoint / skipChargingStation), then waits for
   * the replanned route and reports what is ahead now.
   */
  private async skipNextStop(args: any): Promise<RealtimeToolResult> {
    const fail = (error: string, message: string): RealtimeToolResult => ({ output: { success: false, error, message }, stateChanged: false });

    if (this.waypointService.getSelectedDisplayMode() !== DisplayMode.NAVIGATION) {
      return fail("not_navigating", "Stops can only be skipped during navigation.");
    }
    if (args.type !== "waypoint" && args.type !== "charging_station") {
      return fail("invalid_type", "type must be waypoint or charging_station.");
    }

    const options = this.getSkipOptions();
    if (options.atChargingStop) {
      return fail("at_charging_stop", "Skipping is not available while the car is at a charging stop.");
    }
    if (args.type === "waypoint" && args.waypoint_index != null && args.waypoint_index !== 1) {
      return this.skipLaterWaypoint(args);
    }
    const option = args.type === "waypoint" ? options.waypoint : options.chargingStation;
    if (!option.ahead) {
      return fail("nothing_to_skip", args.type === "waypoint" ? "There is no waypoint ahead to skip." : "There is no charging station ahead to skip.");
    }
    if (!option.allowed) {
      return fail("cannot_skip", "No more charging stations can be skipped: the remaining ones are needed to reach the destination.");
    }
    if (args.confirmed !== true) {
      return fail("confirmation_required", `Ask the user to confirm skipping ${option.name ? `"${option.name}"` : `the next ${args.type.replace('_', ' ')}`}, then call again with confirmed=true.`);
    }

    // Both trigger an immediate replan from the current position.
    await this.applyAndAwaitRoute(() => args.type === "waypoint"
      ? this.navigationService.SkipNextWaypoint()
      : this.navigationService.SkipNextChargingStation());

    if (args.type === "charging_station" && this.navigationService.ObservableCannotSkipMoreChargingStation.value) {
      // The calculation flow opened its "skip failed" dialog and kept the charging stop.
      return fail("skip_failed", "The destination cannot be reached without that charging station, so it was kept.");
    }

    // The new route gets a position on the next GPS update; give it a moment before reporting.
    await firstValueFrom(merge(this.navigationService.ObservableNextTurn.pipe(skip(1)), timer(3000)));
    return {
      output: { success: true, skipped: args.type, name: option.name, route: this.buildRouteInfo() },
      stateChanged: true
    };
  }

  /**
   * Skips a waypoint beyond the next one (waypoint_index from Route points; during navigation
   * passed waypoints are already removed, so 1 is the next one). The destination cannot be skipped.
   */
  private async skipLaterWaypoint(args: any): Promise<RealtimeToolResult> {
    const fail = (error: string, message: string): RealtimeToolResult => ({ output: { success: false, error, message }, stateChanged: false });

    const points = this.waypointService.getRouteList().filter(el => el.wp != null && el.wp >= 0);
    const lastWaypoint = points.length - 2;
    if (!(Number.isInteger(args.waypoint_index) && args.waypoint_index >= 1 && args.waypoint_index <= lastWaypoint)) {
      return fail("invalid_index", lastWaypoint >= 1
        ? `waypoint_index must be 1 to ${lastWaypoint}; the destination cannot be skipped.`
        : "There is no waypoint ahead to skip.");
    }
    const name = points[args.waypoint_index].display_name;
    if (args.confirmed !== true) {
      return fail("confirmation_required", `Ask the user to confirm skipping ${name ? `"${name}"` : `waypoint ${args.waypoint_index}`}, then call again with confirmed=true.`);
    }

    await this.applyAndAwaitRoute(() => this.navigationService.SkipWaypoint(args.waypoint_index));

    // The new route gets a position on the next GPS update; give it a moment before reporting.
    await firstValueFrom(merge(this.navigationService.ObservableNextTurn.pipe(skip(1)), timer(3000)));
    return {
      output: { success: true, skipped: "waypoint", name, route: this.buildRouteInfo() },
      stateChanged: true
    };
  }

  /** What the navigation card's skip button would offer right now. */
  private getSkipOptions(): {
    atChargingStop: boolean,
    waypoint: { ahead: boolean, allowed: boolean, name?: string },
    chargingStation: { ahead: boolean, allowed: boolean, name?: string }
  } {
    const nextTurn = this.navigationService.ObservableNextTurn.value;
    const nextCharger = this.navigationService.getUpcomingRoutePlan()
      ?.find(stop => stop.element.Type === RoutePlanType.Charger)?.element;
    const chargerAhead = nextTurn?.DistToNextChargingStation != null;
    return {
      atChargingStop: this.navigationService.ObservableChargeStateChanged.value !== ChargeState.None,
      waypoint: {
        ahead: nextTurn?.DistToNextWaypoint != null,
        allowed: true,
        name: nextTurn?.NextWaypointDisplayName || undefined
      },
      chargingStation: {
        ahead: chargerAhead,
        // The card greys its charger skip out once a skip could not be planned.
        allowed: !this.navigationService.ObservableCannotSkipMoreChargingStation.value,
        name: nextCharger?.Name
      }
    };
  }

  private async applyAndAwaitRoute(change: () => void): Promise<Record<string, unknown>> {
    const result = this.nextRouteResult();
    change();
    await result;
    return this.buildRouteInfo();
  }

  /** Resolves on the next finished route calculation (success or failure), or after a timeout. */
  private nextRouteResult(): Promise<'ready' | 'failed' | 'timeout'> {
    return firstValueFrom(merge(
      // Both are BehaviorSubjects: skip the current value, wait for the next one.
      this.mapService.ObservableLastRouteData.pipe(skip(1), filter(route => route != null), map(() => 'ready' as const)),
      this.evCalculationService.ObservableFailedPlan.pipe(skip(1), filter(state => state?.failedPlan === true), map(() => 'failed' as const)),
      timer(RealtimeVoiceService.ROUTE_RESULT_TIMEOUT_MS).pipe(map(() => 'timeout' as const))
    ));
  }

  /**
   * Appends a destination like picking a search result and pressing GO does. A place_name is
   * resolved with a background Places search first; the model gets the resolved place back so it
   * can say where it is going, or a failure so it can ask the user to rephrase.
   */
  private async addDestination(args: any): Promise<RealtimeToolResult> {
    let destination: RouteListElement;
    let resolvedPlace: Record<string, unknown> | undefined;

    if (args.saved_place === 'home' || args.saved_place === 'work') {
      const address = this.settingsTools.getSavedAddress(args.saved_place);
      if (!address) {
        return {
          output: { success: false, error: "saved_place_not_set", message: `No ${args.saved_place} address is saved. It can be added in the app settings while signed in.` },
          stateChanged: false
        };
      }
      destination = { display_name: address.Description, lat: address.Location.lat, lng: address.Location.lng };
      resolvedPlace = { name: address.Description, saved_place: args.saved_place, lat: address.Location.lat, lon: address.Location.lng };
    } else if (typeof args.place_name === 'string' && args.place_name.trim()) {
      const place = await this.placeSearchService.findPlace(args.place_name, {
        preferClosest: args.prefer_closest === true
      });
      if (!place) {
        return {
          output: { success: false, error: "place_not_found", message: `No place found for "${args.place_name}".` },
          stateChanged: false
        };
      }
      destination = { display_name: place.name, lat: place.lat, lng: place.lon };
      resolvedPlace = { name: place.name, address: place.address, lat: place.lat, lon: place.lon };
    } else if (Number.isFinite(args.lat) && Number.isFinite(args.lon)) {
      destination = { display_name: '', lat: args.lat, lng: args.lon };
    } else {
      return {
        output: { success: false, error: "missing_destination", message: "Provide saved_place, place_name, or lat and lon." },
        stateChanged: false
      };
    }

    if (this.waypointService.getWaypointsParams().length >= RealtimeVoiceService.MAX_ROUTE_POINTS) {
      return {
        output: { success: false, error: "waypoint_limit_reached", message: `The route already has the maximum of ${RealtimeVoiceService.MAX_ROUTE_POINTS} points.` },
        stateChanged: false
      };
    }

    const route = await this.applyAndAwaitRoute(() => this.waypointService.addEndpoint(destination));
    return { output: { success: true, resolved_place: resolvedPlace, route }, stateChanged: true };
  }

  /** Applies a synchronous tool call to the app. Returns true when app state changed and the session instructions should be refreshed. */
  private handleToolCall(name: string, args: any): boolean {
    switch (name) {

      case "start_navigation":
        if (this.waypointService.ObservableNoRoute.value) {
          return false;
        }
        this.navigationService.ObservableChangeNavigation.next("start");
        return false;

      case "stop_navigation":
        this.navigationService.ObservableChangeNavigation.next("exit");
        return false;

      case "show_on_map": {
        const searchedElement: SearchedRouteElement = {
          display_name: `Location ${args.lat}, ${args.lon}`,
          lat: args.lat,
          lon: args.lon,
          boundingbox: [args.lat + 0.01, args.lon + 0.01, args.lat - 0.01, args.lon - 0.01]
        };
        this.mapService.setSearchedLocation(searchedElement);
        return false;
      }

      case "zoom_in":
        this.mapService.zoomIn(args.level || 1);
        return false;

      case "zoom_out":
        this.mapService.zoomOut(args.level || 1);
        return false;

      case "clear_waypoints": {
        const start = this.waypointService.getWaypointsParams()[0];
        if (start) {
          this.waypointService.setWaypointParams([start]);
        }
        return true;
      }

      case "remove_waypoint":
        if (args.index >= 0 && args.index < this.waypointService.getWaypointsParams().length) {
          this.waypointService.deleteWaypoint(args.index);
          return true;
        }
        this.consoleLoggerService.log("Invalid index for remove_waypoint");
        return false;

      case "set_waypoints":
        this.waypointService.setWaypointParams((args.waypoints || []).map((w: any) => new Waypoint(w.lat, w.lon)));
        return true;

      case "reorder_waypoints": {
        // Reorder the real entries of the route list so display names travel with their coordinates.
        const entries: RouteListElement[] = this.waypointService.getRouteList().filter(el => el.wp != null && el.wp >= 0);
        if (args.from_index >= 0 && args.from_index < entries.length && args.to_index >= 0 && args.to_index < entries.length) {
          const [moved] = entries.splice(args.from_index, 1);
          entries.splice(args.to_index, 0, moved);
          this.waypointService.setRouteListAndRebuild(entries);
          return true;
        }
        this.consoleLoggerService.log("Invalid indices for reorder_waypoints");
        return false;
      }
    }

    return false;
  }

  private async buildSessionPrompt(): Promise<string> {
    const routePoints = JSON.stringify(this.describeRoutePoints());
    const currentPosition = await this.getCurrentPosition();
    const routeInfo = JSON.stringify(this.buildRouteInfo());
    const vehicleState = JSON.stringify(this.vehicleTools.buildVehicleState());
    const appSettings = JSON.stringify(this.settingsTools.buildSettingsState());

    return `
    You are the EVNavigation AI copilot inside an navigation app for ev-s.

    When the user asks something that requires an in-app action,
    call the appropriate tool.

    Speak naturally to the user. HARD RULE: SHORT AND CONCISE RESPONSES.

    HARD RULE: when a request needs a tool, call it right away WITHOUT saying anything first (no
    "Sure, let me plan that"). Speak exactly once, after the tool result, in one or two sentences
    that contain the outcome.

    Example:
    User: "Plan a route to Szentendre"
    Tool: plan_route { place_name: "Szentendre" }   (nothing spoken before the call)
    AI speech (after the tool returns): "Route to Szentendre: 23 km, about 31 minutes, no charging needed."

    Destinations: pass what the user said as place_name (add the city or country when you know it);
    the app looks it up with Google Places. Never guess coordinates for a named place. If the tool
    returns place_not_found, tell the user and ask them to rephrase.

    Home / work: for "home", "my home", "work", "the office" and similar, call plan_route (or
    add_waypoint) with saved_place "home" or "work" instead of place_name; the app uses the saved
    address (home_address / work_address in App settings). If that address is null, or the tool
    returns saved_place_not_set, tell the user to add it in the app settings (they must be signed in).

    Sightseeing / round tours: when the user asks for a tour, YOU design it from your own knowledge
    of the area. Pick the best sights yourself (well-known, reachable by car), order them
    geographically, and call plan_tour once with all of them, without asking the user to list them.
    Use specific names with the city (e.g. "Halászbástya, Budapest"). Set round_trip when the tour
    should end back at the start. A route has at most ${RealtimeVoiceService.MAX_ROUTE_POINTS} points including the start,
    so pick at most ${RealtimeVoiceService.MAX_ROUTE_POINTS - 1} stops, or ${RealtimeVoiceService.MAX_ROUTE_POINTS - 2} for a round trip.
    Afterwards say it in ONE sentence: the stops in order, round trip or not, total distance and time.
    Example: "Your round tour: Buda Castle, Fisherman's Bastion and Heroes' Square, 17 km, about 49 minutes."

    Route questions (distance, time, charging stops, battery on arrival, where and how long to
    charge, weather at the start or at a stop): answer from the route info below, which is exactly what the planning panel shows; call
    get_route_info if it is missing or may be outdated. Times are in minutes: say them as hours and
    minutes. After planning tools, give only a short summary (distance, time, charging stops).

    During navigation (route info status "navigating") everything is measured from the car's current
    position and only the stops still ahead are listed; these values change every second, so ALWAYS
    call get_route_info before answering a navigation question (how far, how long, next charger,
    battery on arrival, when do we arrive). For arrival times add the remaining driving time and the
    remaining charging time to current_time. "charging" describes the charging stop the car is at.
    To skip the next waypoint or charging station use skip_next_stop, only when can_skip allows it
    (otherwise explain why not); confirm with the user first, naming the stop. To skip a waypoint
    that is not the next one ("skip the second waypoint", or one named by the user), pass its
    waypoint_index from Route points; never skip the next waypoint instead.

    Vehicle: the vehicle data below has the active vehicle and all its settings. Change settings
    with set_vehicle_settings (several values in one call). Settings are stored in km/h, bar and %:
    convert from the user's units, and speak values in user_units. "Battery" on its own ("set the
    battery to 80", "my battery is 60%") ALWAYS means the current charge: battery_level. Only
    use battery_reserve when the user explicitly says reserve, safety limit or buffer. The battery
    reserve is per mode: pass battery_reserve and the app applies it to the current mode; add
    battery_reserve_mode only when the user names a mode. If the result lists ignored or limited values, tell the user the
    value that was actually set and the allowed range. Switching to another car, adding, editing or
    removing cars cannot be done by voice: tell the user to use the vehicle settings in the app.

    App settings (the settings dialog): language, voice guidance (spoken navigation instructions),
    light/dark theme, map POIs and distance/temperature/speed/pressure units. Change them with
    set_app_settings, several in one call. For "use imperial/metric units" set all four units.
    Favorite/disabled charging stations, preferred/avoided operators and the home/work address
    cannot be changed by voice: tell the user to use the settings in the app (the saved home/work
    address can still be used as a destination, see above).

    Route points: "start" is where the route begins; it is NOT a waypoint. "waypoints" are the
    stops after the start, in driving order, and the last one is the destination. "The first
    waypoint" / "the first stop" means waypoints index 1, never the start. The index field is what
    remove_waypoint and reorder_waypoints take; index 0 is the start.

Available Data:
- Route points: ${routePoints}
- Current Position: ${currentPosition ? `Lat: ${currentPosition.lat}, Lng: ${currentPosition.lng}` : 'Not available'}
- Route info (planning panel): ${routeInfo}
- Vehicle: ${vehicleState}
- App settings: ${appSettings}
`;
  }

  /**
   * The route list split into the start and the stops after it, so the model does not count the
   * start as the first waypoint. Indices are the waypoint indices the tools take (start = 0).
   */
  private describeRoutePoints(): Record<string, unknown> {
    const [start, ...stops] = this.waypointService.getRouteList().filter(el => el.wp != null && el.wp >= 0);
    return {
      start: start ? { index: 0, name: start.display_name, lat: start.lat, lng: start.lng } : null,
      waypoints: stops.map((el, i) => ({
        index: i + 1,
        name: el.display_name,
        lat: el.lat,
        lng: el.lng,
        ...(i === stops.length - 1 ? { destination: true } : {})
      }))
    };
  }

  /** Last known app position; falls back to asking the browser when the app has none yet. */
  private async getCurrentPosition(): Promise<{ lat: number, lng: number } | null> {
    const lastPosition = this.locationService.getLastPosition();
    if (lastPosition) {
      return { lat: lastPosition.Latitude, lng: lastPosition.Longitude };
    }

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
      });
      return { lat: position.coords.latitude, lng: position.coords.longitude };
    } catch (error) {
      console.error('Failed to get current position:', error);
      return null;
    }
  }

  private createSessionUpdateMessage(instructions: string): { type: string; session: { type: string; instructions: string } } {
    return {
      type: "session.update",
      session: {
        type: "realtime",
        instructions
      }
    };
  }

  /** Shared by plan_route and add_waypoint: a place to look up, or raw coordinates. */
  private destinationToolParameters(): any {
    return {
      type: "object",
      properties: {
        place_name: {
          type: "string",
          description: "Name or address of the destination as the user said it, e.g. 'Szentendre' or 'Andrássy út 10, Budapest'. The app geocodes it with Google Places. Preferred over lat/lon."
        },
        saved_place: {
          type: "string",
          enum: ["home", "work"],
          description: "Use the user's saved home or work address (\"take me home\", \"route to work\"). Takes precedence over place_name."
        },
        prefer_closest: {
          type: "boolean",
          description: "Optional. True only when the user asks for the nearest/closest match ('nearest IKEA'); place_name should then be the place or brand name only."
        },
        lat: { type: "number", description: "Latitude; only when the user gives exact coordinates." },
        lon: { type: "number", description: "Longitude; only when the user gives exact coordinates." }
      }
    };
  }

  private getRealtimeTools(): any[] {
    return [
      ...this.vehicleTools.getToolDefinitions(),
      ...this.settingsTools.getToolDefinitions(),
      {
        type: "function",
        name: "plan_route",
        description: "Plan a route to a destination: it is appended as the new end point after the existing waypoints and the app waits for the recalculated route. On success the output has resolved_place (the place actually found; mention its name) and route (same data as get_route_info).",
        parameters: this.destinationToolParameters()
      },
      {
        type: "function",
        name: "plan_tour",
        description: `Plan a sightseeing or round tour in one call: keeps the start point and replaces all other waypoints with the given stops, in order, then waits for the route. At most ${RealtimeVoiceService.MAX_ROUTE_POINTS - 1} stops (${RealtimeVoiceService.MAX_ROUTE_POINTS - 2} with round_trip), because a route has at most ${RealtimeVoiceService.MAX_ROUTE_POINTS} points including the start; extra stops are dropped and listed in skipped_over_limit. Output: stops (resolved names), not_found (stops that could not be found, if any) and route. Do not speak before calling it.`,
        parameters: {
          type: "object",
          properties: {
            stops: {
              type: "array",
              items: { type: "string" },
              description: "Stop names in visiting order, each specific enough to geocode, with the city (e.g. 'Halászbástya, Budapest')."
            },
            round_trip: {
              type: "boolean",
              description: "True when the tour should end back at the start."
            }
          },
          required: ["stops"]
        }
      },
      {
        type: "function",
        name: "skip_next_stop",
        description: "During navigation, skip a waypoint or the next charging station (like the skip button on the navigation card); the route is replanned from the current position. Without waypoint_index it skips the NEXT waypoint; to skip a later one (e.g. 'skip the second waypoint') pass its waypoint_index. Charging stations: only the next one, when route info can_skip allows it. ALWAYS ask the user to confirm first (name the stop), then call with confirmed=true. The result has the new route info.",
        parameters: {
          type: "object",
          properties: {
            type: { type: "string", enum: ["waypoint", "charging_station"] },
            waypoint_index: {
              type: "number",
              minimum: 1,
              description: "Only for type waypoint: the index of the waypoint to skip from Route points (1 = the next waypoint ahead). Omit to skip the next one. The destination cannot be skipped."
            },
            confirmed: { type: "boolean" }
          },
          required: ["type", "confirmed"]
        }
      },
      {
        type: "function",
        name: "get_route_info",
        description: "Read the planned route exactly as the planning panel shows it: status (ready, calculating, failed, no_route), summary (distance, total_time_min, charging_time_min, charging_stops, total_consumption_kwh) and stops (start, waypoints, chargers with arrival/departure battery %, charge time, plug and power, destination with battery %; each stop may have weather, e.g. 'rain'), plus weather_at_start (temperature, condition, wind_speed, wind_from compass direction) as shown on the wind panel. During navigation (status navigating) it reports from the current position instead: summary (remaining_distance, remaining_driving_time_min, remaining_charging_stops, remaining_charging_time_min, battery_now_percent, speed_limit), current_time, the charging stop the car is at (charging), weather_now and upcoming_stops with distance_from_current and driving_time_from_current_min. Distances are in distance_unit. Waits for a running calculation.",
        parameters: {
          type: "object",
          properties: {}
        }
      },
      {
        type: "function",
        name: "start_navigation",
        description: "Start navigation",
        parameters: {
          type: "object",
          properties: {}
        }
      },
      {
        type: "function",
        name: "stop_navigation",
        description: "Stop navigation",
        parameters: {
          type: "object",
          properties: {}
        }
      },
      {
        type: "function",
        name: "show_on_map",
        description: "Show coordinate on map",
        parameters: {
          type: "object",
          properties: {
            lat: { type: "number" },
            lon: { type: "number" }
          },
          required: ["lat", "lon"]
        }
      },
      {
        type: "function",
        name: "zoom_in",
        description: "Zoom in on the map",
        parameters: {
          type: "object",
          properties: {}
        }
      },
      {
        type: "function",
        name: "zoom_out",
        description: "Zoom out on the map",
        parameters: {
          type: "object",
          properties: {}
        }
      },
      {
        type: "function",
        name: "clear_waypoints",
        description: "Remove all waypoints after the start; the start point is kept.",
        parameters: {
          type: "object",
          properties: {}
        }
      },
      {
        type: "function",
        name: "add_waypoint",
        description: "Add a stop to the route; it becomes the new last point. Use reorder_waypoints afterwards to move it if it should be an intermediate stop. On success the output has resolved_place and route.",
        parameters: this.destinationToolParameters()
      },
      {
        type: "function",
        name: "remove_waypoint",
        description: "Remove a waypoint by its index from Route points. The first waypoint after the start is index 1; the start (index 0) cannot be removed.",
        parameters: {
          type: "object",
          properties: {
            index: {
              type: "number",
              minimum: 1
            }
          },
          required: ["index"]
        }
      },
      {
        type: "function",
        name: "set_waypoints",
        description: "Replace the whole route with the given points. The first point becomes the start, the rest are the waypoints, the last one the destination.",
        parameters: {
          type: "object",
          properties: {
            waypoints: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  lat: { type: "number" },
                  lon: { type: "number" }
                },
                required: ["lat", "lon"]
              }
            }
          },
          required: ["waypoints"]
        }
      },
      {
        type: "function",
        name: "reorder_waypoints",
        description: "Move a route point from one index to another (indices from Route points: 0 is the start, 1 the first waypoint after it). Use index 0 only when the user explicitly wants to change the start point.",
        parameters: {
          type: "object",
          properties: {
            from_index: {
              type: "number",
              minimum: 0
            },
            to_index: {
              type: "number",
              minimum: 0
            }
          },
          required: ["from_index", "to_index"]
        }
      }
    ];
  }

  /**
   * The planned route as the planning panel shows it (TrackInformationComponent summary +
   * RoutePlanComponent stops), in the user's distance unit. `status` tells whether there is a
   * route to talk about at all.
   */
  private buildRouteInfo(): Record<string, unknown> {
    if (this.waypointService.getSelectedDisplayMode() === DisplayMode.NAVIGATION) {
      return this.buildNavigationRouteInfo();
    }
    if (this.waypointService.getSelectedDisplayMode() !== DisplayMode.ROUTE || this.waypointService.ObservableNoRoute.value) {
      return { status: "no_route" };
    }
    if (this.evCalculationService.ObservableFailedPlan.value?.failedPlan) {
      return { status: "failed", message: "Route planning failed; the destination cannot be reached with the current settings." };
    }
    if (this.evCalculationFlowHandler.isRouteCalculating()) {
      return { status: "calculating" };
    }
    const route = this.mapService.getLastRouteData();
    if (!route?.RouteInfo) {
      return { status: "no_route" };
    }

    const imperial = this.settingsService.getUnit().Distance == Unit.Imperial;
    const unit = imperial ? "mi" : "km";
    // RouteInfo.Distance is in metres, RoutePlan distances are already in km (as the panel shows them).
    const fromKm = (km: number) => this.roundDistance(imperial ? km * 0.621371192 : km);
    const info = route.RouteInfo;

    return {
      status: "ready",
      distance_unit: unit,
      summary: {
        distance: fromKm(info.Distance / 1000),
        total_time_min: Math.round(info.TotalTime),
        charging_time_min: Math.round(info.ChargeTime),
        charging_stops: info.ChargingStops,
        total_consumption_kwh: info.TotalConsumption
      },
      weather_at_start: this.buildWeather(route, this.waypointService.getStartCoordsParams()),
      stops: (route.RoutePlan || []).map((el, i) => {
        const stop = this.describeStop(el);
        if (i > 0 && el.DistanceFromPreviousElement != null) {
          stop['distance_from_previous'] = fromKm(el.DistanceFromPreviousElement);
        }
        return stop;
      })
    };
  }

  /**
   * During navigation: what is still ahead, measured from the car's current position like the
   * navigation card (NavigationPlanComponent) does — remaining distance and time, the battery now,
   * the charging stop the car is at, and every remaining stop with its distance and time from here.
   */
  private buildNavigationRouteInfo(): Record<string, unknown> {
    const nextTurn = this.navigationService.ObservableNextTurn.value;
    const upcoming = this.navigationService.getUpcomingRoutePlan();
    const chargeState = this.navigationService.ObservableChargeStateChanged.value;
    const arrivedCharger = this.navigationService.ObservableArrivedToCharger.value;

    const charging = chargeState !== ChargeState.None && arrivedCharger ? {
      state: chargeState === ChargeState.ArriveToCharger ? "arrived" : chargeState === ChargeState.Charging ? "charging" : "finished",
      ...this.describeStop(arrivedCharger)
    } : undefined;

    if (!nextTurn || !upcoming) {
      return { status: "navigating", message: "The current position on the route is not known yet.", charging };
    }

    const units = this.settingsService.getUnit();
    const imperial = units.Distance == Unit.Imperial;
    const fromMetres = (m: number) => this.roundDistance(imperial ? m / 1000 * 0.621371192 : m / 1000);
    const remainingChargers = upcoming.filter(stop => this.isChargerStop(stop.element));
    const position = this.navigationService.CurrentPoint;
    const route = this.mapService.getLastRouteData();
    const now = new Date();

    return {
      status: "navigating",
      measured_from: "current_position",
      current_time: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
      distance_unit: imperial ? "mi" : "km",
      can_skip: this.describeSkipOptions(),
      summary: {
        remaining_distance: fromMetres(nextTurn.DistToDest),
        // Driving time, as on the navigation card; charging time is listed separately.
        remaining_driving_time_min: nextTurn.TimeToDest,
        remaining_charging_stops: remainingChargers.length,
        remaining_charging_time_min: remainingChargers.reduce((sum, stop) =>
          sum + (stop.element.ChargeTimeHour ?? 0) * 60 + (stop.element.ChargeTimeMin ?? 0), 0),
        battery_now_percent: nextTurn.EstimatedBatteryLevel,
        speed_limit: nextTurn.SpeedLimit
          ? Math.round(units.Speed == Unit.Imperial ? this.utilsService.kmphToMph(nextTurn.SpeedLimit) : nextTurn.SpeedLimit)
          : undefined,
        speed_unit: units.Speed == Unit.Imperial ? "mph" : "km/h"
      },
      charging,
      weather_now: route && position ? this.buildWeather(route, { lat: position.Latitude, lng: position.Longitude }) : undefined,
      upcoming_stops: upcoming.map(stop => ({
        ...this.describeStop(stop.element),
        distance_from_current: fromMetres(stop.distanceM),
        driving_time_from_current_min: stop.timeMin
      }))
    };
  }

  private describeSkipOptions(): Record<string, unknown> {
    const options = this.getSkipOptions();
    const usable = (option: { ahead: boolean, allowed: boolean }) => !options.atChargingStop && option.ahead && option.allowed;
    return {
      next_waypoint: usable(options.waypoint),
      next_waypoint_name: options.waypoint.name,
      next_charging_station: usable(options.chargingStation),
      next_charging_station_name: options.chargingStation.name
    };
  }

  /** One route plan element as the route plan panel shows it. */
  private describeStop(el: RoutePlanElement): Record<string, unknown> {
    const stop: Record<string, unknown> = { type: this.routePlanTypeName(el.Type), name: el.Name };
    if (el.OperatorName) {
      stop['operator'] = el.OperatorName;
    }
    if (this.isChargerStop(el)) {
      stop['arrival_battery_percent'] = el.BatteryMinPercent;
      stop['departure_battery_percent'] = el.BatteryMaxPercent;
      stop['charge_time_min'] = (el.ChargeTimeHour ?? 0) * 60 + (el.ChargeTimeMin ?? 0);
      stop['plug'] = this.utilsService.getPlugLabel(el.Icon);
      if (el.ChargePower) {
        stop['charge_power_kw'] = el.ChargePower;
      }
    } else {
      stop['battery_percent'] = el.BatteryLevel;
    }
    if (el.BatteryReservedWarning) {
      stop['below_min_battery_warning'] = true;
    }
    // WeatherImg is only set for subscribers, exactly like the weather chip on the panel.
    const weather = this.weatherCondition(el.WeatherImg);
    if (weather) {
      stop['weather'] = weather;
    }
    return stop;
  }

  private isChargerStop(el: RoutePlanElement): boolean {
    return el.Type === RoutePlanType.Charger || el.Type === RoutePlanType.ChargerEndPoint;
  }

  /** What the wind panel shows: temperature, condition and the wind sample closest to the given point. */
  private buildWeather(route: Route, near: { lat: number, lng: number }): Record<string, unknown> {
    const units = this.settingsService.getUnit();
    const weather: Record<string, unknown> = {};

    if (route.Temperature != null) {
      const fahrenheit = units.Temperature == Unit.Imperial;
      weather['temperature'] = Math.round(fahrenheit ? this.utilsService.ConvertCelsiusToFahrenheit(route.Temperature) : route.Temperature);
      weather['temperature_unit'] = fahrenheit ? "°F" : "°C";
    }

    const condition = this.weatherCondition(this.utilsService.getWeatherImgById(route.WeatherCode));
    if (condition) {
      weather['condition'] = condition;
    }

    const wind = this.windClosestTo(route.Wind, near);
    if (wind?.Speed) {
      const mph = units.Speed == Unit.Imperial;
      weather['wind_speed'] = Math.round(mph ? this.utilsService.kmphToMph(wind.Speed) : wind.Speed);
      weather['wind_speed_unit'] = mph ? "mph" : "km/h";
      // Angle is where the wind comes from (the map's wind particles move the opposite way).
      const compass = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
      weather['wind_from'] = compass[Math.round((((wind.Angle % 360) + 360) % 360) / 45) % 8];
    }

    return weather;
  }

  /** Same pick as MainComponent.updateWindPanel (which uses the start point): the nearest wind sample. */
  private windClosestTo(wind: WindElement[] | undefined, near: { lat: number, lng: number }): WindElement | null {
    if (!wind?.length) {
      return null;
    }
    const distance = (el: WindElement) => this.utilsService.distanceBetweenCoordinates([near.lat, near.lng], [el.Location.lat, el.Location.lng]);
    return wind.reduce((closest, el) => distance(el) < distance(closest) ? el : closest);
  }

  /** "weather_icon_few_clouds_night.svg" -> "few clouds". */
  private weatherCondition(icon: string | null | undefined): string | null {
    if (!icon) {
      return null;
    }
    return icon.replace("weather_icon_", "").replace(".svg", "").replace(/_(day|night)$/, "").replace(/_/g, " ");
  }

  private routePlanTypeName(type: RoutePlanType): string {
    switch (type) {
      case RoutePlanType.StartPoint: return "start";
      case RoutePlanType.WayPoint: return "waypoint";
      case RoutePlanType.Charger: return "charger";
      case RoutePlanType.ChargerEndPoint: return "charger_at_destination";
      case RoutePlanType.EndPoint: return "destination";
    }
  }

  /** Same rounding as the panel's turnValueIntegerThousands pipe: one decimal below 10, else whole. */
  private roundDistance(value: number): number {
    return value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  }

  private resetRealtimeConversationIdleTimeout(): void {
    this.clearRealtimeConversationIdleTimeout();
    this.realtimeConversationIdleTimeoutRef = setTimeout(() => {
      this.stop();
    }, RealtimeVoiceService.REALTIME_CONVERSATION_IDLE_TIMEOUT_MS);
  }

  private clearRealtimeConversationIdleTimeout(): void {
    if (this.realtimeConversationIdleTimeoutRef) {
      clearTimeout(this.realtimeConversationIdleTimeoutRef);
      this.realtimeConversationIdleTimeoutRef = null;
    }
  }

  private handleRealtimeConversationActivityMessage(msg: unknown): void {
    const messageType = this.getRealtimeMessageType(msg);

    if (!messageType) {
      return;
    }

    this.syncRealtimeAssistantOutputState(messageType);

    if ([
      "response.create",
      "input_audio_buffer.speech_started",
      "output_audio_buffer.started",
      "response.output_audio_transcript.delta",
      "response.output_text.delta"
    ].includes(messageType)) {
      this.clearRealtimeConversationIdleTimeout();
      return;
    }

    if ([
      "output_audio_buffer.stopped",
      "output_audio_buffer.cleared",
      "response.output_audio_transcript.done",
      "response.output_text.done",
      "response.done"
    ].includes(messageType) && !this.realtimeAssistantAudioPlaying) {
      // The *.done events arrive when generation ends, while a long answer is still playing;
      // the silence timeout only starts once playback has really stopped.
      this.resetRealtimeConversationIdleTimeout();
    }
  }

  private syncRealtimeAssistantOutputState(messageType: string): void {
    // Over WebRTC, playback runs behind generation: output_audio_buffer.started/stopped mark
    // what the user actually hears, the response events only mark what the server produced.
    if (messageType === "output_audio_buffer.started") {
      this.realtimeAssistantAudioPlaying = true;
      this.realtimeAssistantOutputActive = true;
      return;
    }

    if (messageType === "output_audio_buffer.stopped" || messageType === "output_audio_buffer.cleared") {
      this.realtimeAssistantAudioPlaying = false;
      this.realtimeAssistantOutputActive = false;
      return;
    }

    if ([
      "response.output_audio_transcript.delta",
      "response.output_text.delta"
    ].includes(messageType)) {
      this.realtimeAssistantOutputActive = true;
      return;
    }

    if ([
      "response.output_audio.done",
      "response.output_audio_transcript.done",
      "response.done"
    ].includes(messageType) && !this.realtimeAssistantAudioPlaying) {
      this.realtimeAssistantOutputActive = false;
    }
  }

  private getRealtimeMessageType(msg: unknown): string | null {
    if (!msg || typeof msg !== 'object' || !("type" in msg)) {
      return null;
    }

    const { type } = msg as { type?: unknown };
    return typeof type === 'string' ? type : null;
  }

  private debugLog(label: string, value: unknown): void {
    if (RealtimeVoiceService.REALTIME_DEBUG_LOGGING_ENABLED) {
      console.log(label, value);
    }
  }
}
